/**
 * The localized option lists the forms offer, one per contract vocabulary.
 *
 * ── THE WORDS ARE THE CONTRACT'S, NOT A2'S ──────────────────────────────────
 * A2's option labels were free text over a mock store ("Hipertrofia", five
 * goals, 75 minutes). The server stores the contract's enums (seven goals,
 * `30 | 45 | 60 | 90 | 120` minutes, 1–5 sleep quality), so each list is the
 * enum, labelled with the words the intake already uses for it (the `core`
 * catalogue's `onboarding.*`, `bodymap.*`, `occupation.*`… keys). Only what
 * `core` does not say lives in `profile-editors`. Called at render, so the
 * active locale is read each time; imported by name, never through `m`.
 */
import type {
  AthleteSex,
  EquipmentPlace,
  Goal,
  Occupation,
  PainTrend,
  PreviousModality,
  ProteinMeals,
  ProteinSupplement,
  SessionMinutes,
} from "@repo/contract/training";
import {
  "bodymap.age.6mto2y" as age6mto2y,
  "bodymap.age.gt2y" as ageGt2y,
  "bodymap.age.lt6m" as ageLt6m,
  "profile-editors.pain_area_abdomen" as areaAbdomen,
  "profile-editors.pain_area_ankle" as areaAnkle,
  "profile-editors.pain_area_cervical" as areaCervical,
  "profile-editors.pain_area_chest" as areaChest,
  "profile-editors.pain_area_elbow" as areaElbow,
  "profile-editors.pain_area_hip" as areaHip,
  "profile-editors.pain_area_knee" as areaKnee,
  "profile-editors.pain_area_lumbar" as areaLumbar,
  "profile-editors.pain_area_shoulder" as areaShoulder,
  "profile-editors.pain_area_thoracic" as areaThoracic,
  "profile-editors.pain_area_wrist" as areaWrist,
  "onboarding.experience_consistent_for.1to3y" as consistent1to3y,
  "onboarding.experience_consistent_for.3to6m" as consistent3to6m,
  "onboarding.experience_consistent_for.6mto1y" as consistent6mto1y,
  "onboarding.experience_consistent_for.gt3y" as consistentGt3y,
  "onboarding.experience_consistent_for.lt3m" as consistentLt3m,
  "onboarding.experience.current" as experienceCurrent,
  "profile-editors.experience_current_hint" as experienceCurrentHint,
  "onboarding.experience.never" as experienceNever,
  "profile-editors.experience_never_hint" as experienceNeverHint,
  "onboarding.experience.stopped" as experienceStopped,
  "profile-editors.experience_stopped_hint" as experienceStoppedHint,
  "onboarding.goal.event" as goalEvent,
  "onboarding.goal.fat_loss" as goalFatLoss,
  "onboarding.goal.general_health" as goalGeneralHealth,
  "onboarding.goal.hypertrophy" as goalHypertrophy,
  "onboarding.goal.recomposition" as goalRecomposition,
  "onboarding.goal.return" as goalReturn,
  "onboarding.goal.strength" as goalStrength,
  "onboarding.session_hard_limit.hard" as limitHard,
  "onboarding.session_hard_limit.preference" as limitPreference,
  "onboarding.session_minutes.30" as minutes30,
  "onboarding.session_minutes.45" as minutes45,
  "onboarding.session_minutes.60" as minutes60,
  "onboarding.session_minutes.90" as minutes90,
  "onboarding.session_minutes.120" as minutes120,
  "modality.bodybuilding" as modalityBodybuilding,
  "modality.calisthenics" as modalityCalisthenics,
  "modality.crossfit" as modalityCrossfit,
  "modality.endurance" as modalityEndurance,
  "modality.martial_arts" as modalityMartialArts,
  "modality.none" as modalityNone,
  "modality.other" as modalityOther,
  "modality.powerlifting" as modalityPowerlifting,
  "modality.team_sport" as modalityTeamSport,
  "modality.weights" as modalityWeights,
  "bodymap.nature.acute" as natureAcute,
  "bodymap.nature.chronic" as natureChronic,
  "bodymap.nature.inflammation" as natureInflammation,
  "bodymap.nature.limitation" as natureLimitation,
  "occupation.active" as occupationActive,
  "occupation.heavy" as occupationHeavy,
  "occupation.sedentary" as occupationSedentary,
  "occupation.standing" as occupationStanding,
  "onboarding.equipment_place.gym" as placeGym,
  "onboarding.equipment_place.home" as placeHome,
  "onboarding.equipment_place.mix" as placeMix,
  "onboarding.equipment_place.outdoors" as placeOutdoors,
  "protein_meals.four_plus" as proteinFourPlus,
  "protein_meals.three" as proteinThree,
  "protein_meals.two" as proteinTwo,
  "protein_meals.zero_to_one" as proteinZeroToOne,
  "onboarding.secondary_goal.none" as secondaryNone,
  "profile-editors.session_30_hint" as session30Hint,
  "profile-editors.session_90_hint" as session90Hint,
  "onboarding.sex.female" as sexFemale,
  "onboarding.sex.male" as sexMale,
  "onboarding.sex.other" as sexOther,
  "profile-editors.side_both" as sideBoth,
  "profile-editors.side_left" as sideLeft,
  "profile-editors.side_right" as sideRight,
  "profile-editors.pain_since_half_year" as sinceHalfYear,
  "profile-editors.pain_since_month" as sinceMonth,
  "profile-editors.pain_since_two_months" as sinceTwoMonths,
  "profile-editors.pain_since_week" as sinceWeek,
  "profile-editors.sleep_quality_1" as sleep1,
  "profile-editors.sleep_quality_2" as sleep2,
  "profile-editors.sleep_quality_3" as sleep3,
  "profile-editors.sleep_quality_4" as sleep4,
  "profile-editors.sleep_quality_5" as sleep5,
  "profile-editors.split_arms" as splitArms,
  "profile-editors.split_full_body" as splitFullBody,
  "profile-editors.split_legs" as splitLegs,
  "profile-editors.split_lower" as splitLower,
  "profile-editors.split_pull" as splitPull,
  "profile-editors.split_push" as splitPush,
  "profile-editors.split_upper" as splitUpper,
  "bodymap.status.in_treatment" as statusInTreatment,
  "bodymap.status.resolved" as statusResolved,
  "bodymap.status.resolved_limited" as statusResolvedLimited,
  "onboarding.experience_stopped_ago.1to3y" as stopped1to3y,
  "onboarding.experience_stopped_ago.3to12m" as stopped3to12m,
  "onboarding.experience_stopped_ago.gt3y" as stoppedGt3y,
  "onboarding.experience_stopped_ago.lt3m" as stoppedLt3m,
  "protein_supplement.no" as supplementNo,
  "protein_supplement.regular" as supplementRegular,
  "protein_supplement.sometimes" as supplementSometimes,
  "onboarding.experience_trained_for.6mto2y" as trained6mto2y,
  "onboarding.experience_trained_for.gt2y" as trainedGt2y,
  "onboarding.experience_trained_for.lt6m" as trainedLt6m,
  "profile-editors.pain_trend_improving" as trendImproving,
  "profile-editors.pain_trend_same" as trendSame,
  "profile-editors.pain_trend_worsening" as trendWorsening,
  "bodymap.type.disc" as typeDisc,
  "bodymap.type.fracture" as typeFracture,
  "bodymap.type.ligament" as typeLigament,
  "bodymap.type.other" as typeOther,
  "bodymap.type.surgery" as typeSurgery,
  "bodymap.type.tendon" as typeTendon,
  "bodymap.when.after" as whenAfter,
  "bodymap.when.always" as whenAlways,
  "bodymap.when.movement" as whenMovement,
  "bodymap.when.rest" as whenRest,
} from "@repo/i18n";
import type {
  InjuryAge,
  InjuryMoment,
  InjuryNature,
  InjuryStatus,
  InjuryType,
  PainArea,
  SinceBucket,
} from "./pain-model";
import type { SplitDay, SplitKind } from "./routine-helpers";

export interface LabelledOption<T extends string> {
  readonly description?: string;
  readonly label: string;
  readonly value: T;
}

type Labels<T extends string> = Readonly<Record<T, () => string>>;

const list = <T extends string>(
  order: readonly T[],
  labels: Labels<T>,
  hints?: Partial<Record<T, () => string>>
): LabelledOption<T>[] =>
  order.map((value) => {
    const hint = hints?.[value];
    return hint
      ? { description: hint(), label: labels[value](), value }
      : { label: labels[value](), value };
  });

export const sexOptions = () =>
  list<AthleteSex>(["female", "male", "other"], {
    female: sexFemale,
    male: sexMale,
    other: sexOther,
  });

export type DominantSide = "both" | "left" | "right";

export const sideOptions = () =>
  list<DominantSide>(["right", "left", "both"], {
    both: sideBoth,
    left: sideLeft,
    right: sideRight,
  });

const GOAL_LABELS: Labels<Goal> = {
  event: goalEvent,
  fat_loss: goalFatLoss,
  general_health: goalGeneralHealth,
  hypertrophy: goalHypertrophy,
  recomposition: goalRecomposition,
  return: goalReturn,
  strength: goalStrength,
};

const GOAL_ORDER: readonly Goal[] = [
  "hypertrophy",
  "strength",
  "fat_loss",
  "recomposition",
  "general_health",
  "return",
  "event",
];

export const goalOptions = () => list(GOAL_ORDER, GOAL_LABELS);

/** "None" first, then every goal but the primary (A2 editors.tsx:241). */
export type SecondaryChoice = Goal | "none";

export function secondaryOptions(
  primary: Goal | null
): LabelledOption<SecondaryChoice>[] {
  return [
    { label: secondaryNone(), value: "none" },
    ...list(
      GOAL_ORDER.filter((goal) => goal !== primary),
      GOAL_LABELS
    ),
  ];
}

export type ExperienceStatus = "current" | "never" | "stopped";

export const experienceStatusOptions = () =>
  list<ExperienceStatus>(
    ["never", "stopped", "current"],
    {
      current: experienceCurrent,
      never: experienceNever,
      stopped: experienceStopped,
    },
    {
      current: experienceCurrentHint,
      never: experienceNeverHint,
      stopped: experienceStoppedHint,
    }
  );

export type ConsistentFor = "1to3y" | "3to6m" | "6mto1y" | "gt3y" | "lt3m";

export const consistentForOptions = () =>
  list<ConsistentFor>(["lt3m", "3to6m", "6mto1y", "1to3y", "gt3y"], {
    "1to3y": consistent1to3y,
    "3to6m": consistent3to6m,
    "6mto1y": consistent6mto1y,
    gt3y: consistentGt3y,
    lt3m: consistentLt3m,
  });

export type StoppedAgo = "1to3y" | "3to12m" | "gt3y" | "lt3m";

export const stoppedAgoOptions = () =>
  list<StoppedAgo>(["lt3m", "3to12m", "1to3y", "gt3y"], {
    "1to3y": stopped1to3y,
    "3to12m": stopped3to12m,
    gt3y: stoppedGt3y,
    lt3m: stoppedLt3m,
  });

export type TrainedFor = "6mto2y" | "gt2y" | "lt6m";

export const trainedForOptions = () =>
  list<TrainedFor>(["lt6m", "6mto2y", "gt2y"], {
    "6mto2y": trained6mto2y,
    gt2y: trainedGt2y,
    lt6m: trainedLt6m,
  });

export type SessionMinutesChoice = `${SessionMinutes}`;

export const sessionMinutesOptions = () =>
  list<SessionMinutesChoice>(
    ["30", "45", "60", "90", "120"],
    {
      "30": minutes30,
      "45": minutes45,
      "60": minutes60,
      "90": minutes90,
      "120": minutes120,
    },
    { "30": session30Hint, "90": session90Hint }
  );

export type HardLimit = "hard" | "preference";

export const hardLimitOptions = () =>
  list<HardLimit>(["preference", "hard"], {
    hard: limitHard,
    preference: limitPreference,
  });

export const placeOptions = () =>
  list<EquipmentPlace>(["home", "gym", "mix", "outdoors"], {
    gym: placeGym,
    home: placeHome,
    mix: placeMix,
    outdoors: placeOutdoors,
  });

export const occupationOptions = () =>
  list<Occupation>(["sedentary", "standing", "active", "heavy"], {
    active: occupationActive,
    heavy: occupationHeavy,
    sedentary: occupationSedentary,
    standing: occupationStanding,
  });

export const proteinMealsOptions = () =>
  list<ProteinMeals>(["zero_to_one", "two", "three", "four_plus"], {
    four_plus: proteinFourPlus,
    three: proteinThree,
    two: proteinTwo,
    zero_to_one: proteinZeroToOne,
  });

export const supplementOptions = () =>
  list<ProteinSupplement>(["no", "sometimes", "regular"], {
    no: supplementNo,
    regular: supplementRegular,
    sometimes: supplementSometimes,
  });

export const modalityOptions = () =>
  list<PreviousModality>(
    [
      "weights",
      "bodybuilding",
      "powerlifting",
      "crossfit",
      "calisthenics",
      "endurance",
      "team_sport",
      "martial_arts",
      "other",
      "none",
    ],
    {
      bodybuilding: modalityBodybuilding,
      calisthenics: modalityCalisthenics,
      crossfit: modalityCrossfit,
      endurance: modalityEndurance,
      martial_arts: modalityMartialArts,
      none: modalityNone,
      other: modalityOther,
      powerlifting: modalityPowerlifting,
      team_sport: modalityTeamSport,
      weights: modalityWeights,
    }
  );

export type SleepQuality = "1" | "2" | "3" | "4" | "5";

export const sleepQualityOptions = () =>
  list<SleepQuality>(["1", "2", "3", "4", "5"], {
    "1": sleep1,
    "2": sleep2,
    "3": sleep3,
    "4": sleep4,
    "5": sleep5,
  });

export const natureOptions = () =>
  list<InjuryNature>(["acute", "chronic", "limitation", "inflammation"], {
    acute: natureAcute,
    chronic: natureChronic,
    inflammation: natureInflammation,
    limitation: natureLimitation,
  });

export const momentOptions = () =>
  list<InjuryMoment>(["movement", "after", "rest", "always"], {
    after: whenAfter,
    always: whenAlways,
    movement: whenMovement,
    rest: whenRest,
  });

export const injuryTypeOptions = () =>
  list<InjuryType>(
    ["fracture", "ligament", "tendon", "disc", "surgery", "other"],
    {
      disc: typeDisc,
      fracture: typeFracture,
      ligament: typeLigament,
      other: typeOther,
      surgery: typeSurgery,
      tendon: typeTendon,
    }
  );

export const injuryStatusOptions = () =>
  list<InjuryStatus>(["resolved", "resolved_limited", "in_treatment"], {
    in_treatment: statusInTreatment,
    resolved: statusResolved,
    resolved_limited: statusResolvedLimited,
  });

export const injuryAgeOptions = () =>
  list<InjuryAge>(["lt6m", "6mto2y", "gt2y"], {
    "6mto2y": age6mto2y,
    gt2y: ageGt2y,
    lt6m: ageLt6m,
  });

/** A2's "Desde quando?" (editors.tsx:1015-1022), shortest first. */
export const sinceOptions = () =>
  list<SinceBucket>(["week", "month", "two_months", "half_year"], {
    half_year: sinceHalfYear,
    month: sinceMonth,
    two_months: sinceTwoMonths,
    week: sinceWeek,
  });

/** A2's "Como está evoluindo?" (editors.tsx:1051-1055). */
export const trendOptions = () =>
  list<PainTrend>(["improving", "same", "worsening"], {
    improving: trendImproving,
    same: trendSame,
    worsening: trendWorsening,
  });

const AREA_LABELS: Labels<PainArea> = {
  abdomen: areaAbdomen,
  ankle: areaAnkle,
  cervical: areaCervical,
  chest: areaChest,
  elbow: areaElbow,
  hip: areaHip,
  knee: areaKnee,
  lumbar: areaLumbar,
  shoulder: areaShoulder,
  thoracic: areaThoracic,
  wrist: areaWrist,
};

export const painAreaLabel = (area: PainArea) => AREA_LABELS[area]();

const SPLIT_LABELS: Labels<SplitKind> = {
  arms: splitArms,
  full_body: splitFullBody,
  legs: splitLegs,
  lower: splitLower,
  pull: splitPull,
  push: splitPush,
  upper: splitUpper,
};

export const splitLabel = (kind: SplitKind) => SPLIT_LABELS[kind]();

/** A previewed day's name: its kind's, or a custom day's own label. */
export const dayName = (day: SplitDay) =>
  day.kind === null ? (day.name ?? "") : splitLabel(day.kind);
